"use client";

import { useCallback, useMemo, useRef, useState } from "react";

/**
 * Ordered steps of the public-event signup funnel. Transitions are not strictly
 * linear — login may skip straight to select-attendee, and select-attendee
 * branches to adult vs child details — so navigation is driven by an explicit
 * history stack rather than an index.
 */
export type SignupStep =
  | "login"
  | "two-factor"
  | "create-account"
  | "confirm-email"
  | "select-attendee"
  | "details"
  | "success";

/**
 * Attendee branch chosen on the select-attendee step. Defined here (a
 * client-safe module) rather than in the server-only Spond client so the
 * select-attendee UI can reference it without crossing the server boundary.
 */
export type AttendeeType = "SELF" | "CHILD";

export interface UseSignupFunnelOptions {
  initialStep?: SignupStep;
}

export interface SignupFunnelController {
  step: SignupStep;
  canGoBack: boolean;
  goTo: (step: SignupStep, options?: { replace?: boolean }) => void;
  back: () => void;
  reset: () => void;
}

/**
 * Step state machine for the signup bottom sheet. Keeps a history stack so
 * `back()` retraces the exact path taken (correct across the funnel's
 * branches), and `reset()` clears it — e.g. when the sheet is closed.
 */
export function useSignupFunnel(
  options: UseSignupFunnelOptions = {},
): SignupFunnelController {
  // Capture the initial step once: `reset()` must return to the step the
  // funnel was first mounted with, not whatever `options.initialStep` happens
  // to be on a later render — otherwise reset could jump to a step the history
  // root never contained, desyncing `back()`.
  const initialStepRef = useRef(options.initialStep ?? "login");
  const initialStep = initialStepRef.current;
  const [history, setHistory] = useState<SignupStep[]>([initialStep]);

  // `replace` swaps the current step instead of pushing, so back() cannot
  // retrace into a consumed step (e.g. a verified 2FA prompt).
  const goTo = useCallback(
    (step: SignupStep, options?: { replace?: boolean }) => {
      setHistory((prev) =>
        options?.replace ? [...prev.slice(0, -1), step] : [...prev, step],
      );
    },
    [],
  );

  const back = useCallback(() => {
    setHistory((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const reset = useCallback(() => {
    setHistory([initialStep]);
  }, [initialStep]);

  const step = history[history.length - 1];
  const canGoBack = history.length > 1;

  return useMemo(
    () => ({ step, canGoBack, goTo, back, reset }),
    [step, canGoBack, goTo, back, reset],
  );
}
